import { Trophy } from "lucide-react";
import { useTranslate } from "ra-core";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  gameMission,
  scoreOf,
  type GameMissionKey,
  type PlayableKey,
} from "../domain/training/missions";
import type { MissionView } from "../domain/training/progress";
import { QUIZZES } from "./content";
import type { QuizQuestion } from "./content/types";
import { QUEUE_ROUNDS } from "./content/readQueue";
import { Celebration } from "./Celebration";
import { HotkeyGame, type HotkeyResult } from "./games/HotkeyGame";
import { OrderGame } from "./games/OrderGame";
import { QuizGame } from "./games/QuizGame";
import { useRecordAttempt } from "./useTraining";

type Result = {
  score: number;
  correct: number;
  total: number;
  hotkeys?: HotkeyResult;
};

/**
 * Plays one game mission, records the attempt (best score kept server-side), then shows the score card with the
 * XP gained. A first pass triggers the celebration. « Rejouer » restarts the game; « Retour » goes back to the map.
 */
export const GameRunner = ({
  missionKey,
  view,
  onExit,
  questions,
  explanationLink,
}: {
  missionKey: PlayableKey;
  /** The mission's state when the game opened (XP before, best score). */
  view: MissionView;
  onExit: () => void;
  /** Replaces the built-in questions (the product course builds them from the live catalog). */
  questions?: readonly QuizQuestion[];
  explanationLink?: { to: string; label: string };
}) => {
  const t = useTranslate();
  const mission = gameMission(missionKey);
  const { mutate, isPending, isError } = useRecordAttempt();
  const [run, setRun] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const [gained, setGained] = useState<number | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  // XP and state before this game session (the view refreshes after each save)
  const before = useRef({
    xp: view.xp,
    done: view.state === "done",
    best: view.bestScore,
  });
  const resultRef = useRef<HTMLHeadingElement>(null);
  const title = t(`gavion.training.missions.${missionKey}.title`);

  const save = useCallback(
    (r: Result) => {
      setResult(r);
      setGained(null);
      mutate(
        { key: missionKey, score: r.score },
        {
          onSuccess: (row) => {
            setGained(Math.max(0, row.xp - before.current.xp));
            if (!before.current.done && row.completed_at) setCelebrate(true);
            before.current = {
              xp: Math.max(before.current.xp, row.xp),
              done: before.current.done || !!row.completed_at,
              best: Math.max(before.current.best ?? 0, row.best_score),
            };
          },
        },
      );
    },
    [missionKey, mutate],
  );
  useEffect(() => {
    if (result) resultRef.current?.focus();
  }, [result]);

  // a quiz without questions (the product catalog failed or is refetching): back to the map, never an empty QuizGame
  // the list is frozen when the game opens: a catalog refetch / admin edit must not change a running quiz
  // (« Rejouer » replays the same list; leaving to the map and playing again reads the latest one)
  const [quizQuestions] = useState(
    () => questions ?? QUIZZES[missionKey as GameMissionKey] ?? [],
  );
  const noQuestions =
    mission.game !== "order" &&
    mission.game !== "hotkeys" &&
    quizQuestions.length === 0;
  useEffect(() => {
    if (noQuestions && !result) onExit();
  }, [noQuestions, result, onExit]);

  const finishQuiz = (correct: number, total: number) =>
    save({ score: scoreOf(correct, total), correct, total });
  const finishHotkeys = useCallback(
    (h: HotkeyResult) =>
      save({ score: h.score, correct: h.correct, total: h.total, hotkeys: h }),
    [save],
  );
  const replay = () => {
    setResult(null);
    setRun((n) => n + 1);
  };
  const stopCelebrating = useCallback(() => setCelebrate(false), []);

  const game = () => {
    if (mission.game === "order")
      return (
        <OrderGame
          key={run}
          rounds={QUEUE_ROUNDS}
          onFinish={finishQuiz}
          onQuit={onExit}
        />
      );
    if (mission.game === "hotkeys")
      return (
        <HotkeyGame
          key={run}
          best={before.current.best}
          onFinish={finishHotkeys}
          onQuit={onExit}
        />
      );
    return (
      <QuizGame
        key={run}
        questions={quizQuestions}
        explanationLink={explanationLink}
        onFinish={finishQuiz}
        onQuit={onExit}
      />
    );
  };

  if (noQuestions && !result) return null;

  return (
    <div className="flex flex-col gap-4">
      {celebrate && (
        <Celebration
          message={t("gavion.training.game.celebration", { mission: title })}
          onDone={stopCelebrating}
        />
      )}
      <h2 className="text-xl font-extrabold">{title}</h2>
      {result ? (
        <ResultCard
          headingRef={resultRef}
          missionKey={missionKey}
          result={result}
          gained={gained}
          saving={isPending}
          failed={isError}
          previousBest={before.current.best}
          onRetrySave={() => save(result)}
          onReplay={replay}
          onBack={onExit}
        />
      ) : (
        game()
      )}
    </div>
  );
};

const ResultCard = ({
  headingRef,
  missionKey,
  result,
  gained,
  saving,
  failed,
  previousBest,
  onRetrySave,
  onReplay,
  onBack,
}: {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  missionKey: PlayableKey;
  result: Result;
  gained: number | null;
  saving: boolean;
  failed: boolean;
  previousBest: number | null;
  onRetrySave: () => void;
  onReplay: () => void;
  onBack: () => void;
}) => {
  const t = useTranslate();
  const m = gameMission(missionKey);
  const passed = result.score >= m.pass;
  const perfect = result.score >= 100 && m.perfectBonus > 0;
  const h = result.hotkeys;
  return (
    <section
      className={cn(
        "flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:p-6 motion-safe:animate-in motion-safe:fade-in",
        passed && "border-primary",
      )}
    >
      <h3
        ref={headingRef}
        tabIndex={-1}
        className="flex items-center gap-2 text-lg font-extrabold outline-none"
      >
        <Trophy
          className={cn(
            "size-5",
            passed ? "text-primary" : "text-muted-foreground",
          )}
          aria-hidden="true"
        />
        {t("gavion.training.game.score_title")}
      </h3>
      {h ? (
        <div className="space-y-1">
          <p className="text-3xl font-extrabold tabular-nums">
            {t("gavion.training.hotkeys.score", { score: result.score })}
          </p>
          <p className="text-sm">
            {t("gavion.training.hotkeys.accuracy", {
              percent: scoreOf(h.correct, h.total),
            })}
            {h.avgMs !== null &&
              ` · ${t("gavion.training.hotkeys.speed", {
                seconds: (h.avgMs / 1000).toFixed(1),
              })}`}
          </p>
          {previousBest !== null && result.score > previousBest && (
            <p className="text-sm font-bold text-primary">
              {t("gavion.training.hotkeys.new_record")}
            </p>
          )}
        </div>
      ) : (
        <p className="text-3xl font-extrabold tabular-nums">
          {t("gavion.training.game.score", {
            correct: result.correct,
            total: result.total,
            score: result.score,
          })}
        </p>
      )}
      <p className="font-semibold">
        {passed
          ? t("gavion.training.game.passed")
          : t("gavion.training.game.failed", { pass: m.pass })}
      </p>
      {perfect && (
        <p className="text-sm">
          {t("gavion.training.game.perfect", { xp: m.perfectBonus })}
        </p>
      )}
      <div role="status" aria-live="polite" className="text-sm">
        {saving && t("gavion.training.game.saving")}
        {!saving && gained !== null && gained > 0 && (
          <span className="inline-flex rounded-full bg-primary px-3 py-1 font-extrabold text-primary-foreground motion-safe:animate-in motion-safe:zoom-in-75">
            {t("gavion.training.game.xp_gain", { xp: gained })}
          </span>
        )}
        {failed && (
          <span className="flex flex-wrap items-center gap-2 text-destructive">
            {t("gavion.training.game.save_failed")}
            <Button size="sm" variant="outline" onClick={onRetrySave}>
              {t("gavion.training.retry")}
            </Button>
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onReplay} variant={passed ? "outline" : "default"}>
          {t("gavion.training.game.retry")}
        </Button>
        <Button onClick={onBack} variant={passed ? "default" : "outline"}>
          {t("gavion.training.game.back")}
        </Button>
      </div>
    </section>
  );
};
